import React, { useCallback, useEffect, useState } from 'react';
import {
  getSavedPrinter,
  savePrinter,
  clearPrinter,
  printBytes,
  getSavedLabelPrinter,
  saveLabelPrinter,
  clearLabelPrinter,
  SavedPrinter,
} from '../services/usbPrinter';
import { buildTestEscPos } from '../services/escpos';
import { printLabelSpec, resolveLabelTarget } from '../services/labelPrint';
import {
  isNativeApp,
  nativeListDevices,
  nativeRequestPermission,
  NativeUsbDevice,
} from '../services/nativeUsb';
import { usePos } from '../context/PosContext';
import './Sections.css';

// USB device list (usb_link.xml / dialog_choose_usb_device.xml). The Android app
// enumerates raw USB devices for printers/scanners; the web equivalent is the
// WebUSB API (navigator.usb), available on localhost/HTTPS in Chromium browsers.
// We list previously-authorized devices and let the user grant access to a new
// one via the browser's device picker.

// Minimal WebUSB typings (not in the default DOM lib) for the bits we use.
interface WebUsbDevice {
  productName?: string;
  manufacturerName?: string;
  serialNumber?: string;
  vendorId: number;
  productId: number;
  opened: boolean;
  open(): Promise<void>;
  close(): Promise<void>;
  forget?(): Promise<void>;
}
interface WebUsb {
  getDevices(): Promise<WebUsbDevice[]>;
  requestDevice(opts: { filters: unknown[] }): Promise<WebUsbDevice>;
}

const getUsb = (): WebUsb | undefined =>
  (navigator as unknown as { usb?: WebUsb }).usb;

const hex = (n: number) => '0x' + n.toString(16).padStart(4, '0');

const UsbLink: React.FC = () => {
  const { config } = usePos();
  const usb = getUsb();
  const native = isNativeApp(); // Android app → native USB plugin, not WebUSB
  const [devices, setDevices] = useState<WebUsbDevice[]>([]);
  const [nativeDevices, setNativeDevices] = useState<NativeUsbDevice[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [printer, setPrinter] = useState<SavedPrinter | null>(() => getSavedPrinter());
  const [labelPrinter, setLabelPrinter] = useState<SavedPrinter | null>(() => getSavedLabelPrinter());

  // Same identity rule as the transport: prefer the per-unit id (Android USB
  // path / USB serial) so a bill printer and a label printer of the same model
  // are never mistaken for each other.
  const sameDevice = (
    saved: SavedPrinter | null,
    d: { vendorId: number; productId: number; serialNumber?: string; deviceName?: string }
  ) => {
    if (!saved) return false;
    if (saved.deviceName && d.deviceName) return saved.deviceName === d.deviceName;
    if (saved.serialNumber && d.serialNumber) return saved.serialNumber === d.serialNumber;
    return saved.vendorId === d.vendorId && saved.productId === d.productId;
  };

  const isPrinter = (d: WebUsbDevice) => sameDevice(printer, d);
  const isLabelPrinter = (d: { vendorId: number; productId: number; deviceName?: string; serialNumber?: string }) =>
    sameDevice(labelPrinter, d);

  const setAsPrinter = (d: WebUsbDevice) => {
    const p: SavedPrinter = {
      vendorId: d.vendorId,
      productId: d.productId,
      serialNumber: d.serialNumber,
      label: d.productName || 'USB Printer',
    };
    savePrinter(p);
    setPrinter(p);
    setInfo(`Receipts & KOTs will print to ${p.label}.`);
  };

  // Label (barcode) printer slot — TSC/TSPL device for barcode labels only.
  const setAsLabelPrinter = async (d: {
    vendorId: number;
    productId: number;
    productName?: string;
    deviceName?: string;
    serialNumber?: string;
  }) => {
    setError(null);
    setInfo(null);
    if (native) {
      const granted = await nativeRequestPermission(d.vendorId, d.productId, d.deviceName).catch(() => false);
      if (!granted) {
        setError('USB permission was denied for that device.');
        return;
      }
    }
    const p: SavedPrinter = {
      vendorId: d.vendorId,
      productId: d.productId,
      deviceName: d.deviceName,
      serialNumber: d.serialNumber,
      label: d.productName || 'USB Label Printer',
    };
    saveLabelPrinter(p);
    setLabelPrinter(p);
    setInfo(`Barcode labels will print to ${p.label}.`);
  };

  const unsetLabelPrinter = () => {
    clearLabelPrinter();
    setLabelPrinter(null);
    setInfo('Label printer cleared — barcode labels fall back to the browser print sheet.');
  };

  // Test label — goes wherever real labels go (label printer if set, else the
  // receipt printer) in whichever language that printer speaks.
  const testLabelPrint = async () => {
    setError(null);
    setInfo(null);
    const target = resolveLabelTarget();
    try {
      await printLabelSpec({
        widthMm: 35,
        heightMm: 15,
        position: 'Bottom',
        header: config.storeName || 'EPOS',
        name: 'TEST LABEL',
        rows: [],
        code: '1234567890',
        copies: 1,
      });
      setInfo(
        `Test label sent to ${target?.printer.label || 'the USB printer'} as ${
          target?.lang === 'tspl' ? 'TSPL' : 'ESC/POS'
        }.`
      );
    } catch (e) {
      setError((e as Error).message);
    }
  };

  // Native (Android) flow: the system USB-permission dialog stands in for the
  // browser's device picker, then the device is saved as the default printer.
  const setAsNativePrinter = async (d: NativeUsbDevice) => {
    setError(null);
    setInfo(null);
    try {
      const granted = await nativeRequestPermission(d.vendorId, d.productId, d.deviceName);
      if (!granted) {
        setError('USB permission was denied for that device.');
        return;
      }
      const p: SavedPrinter = {
        vendorId: d.vendorId,
        productId: d.productId,
        deviceName: d.deviceName,
        label: d.productName || 'USB Printer',
      };
      savePrinter(p);
      setPrinter(p);
      setInfo(`Receipts & KOTs will print to ${p.label}.`);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const unsetPrinter = () => {
    clearPrinter();
    setPrinter(null);
    setInfo('Default printer cleared — printing falls back to the browser.');
  };

  const testPrint = async () => {
    setError(null);
    setInfo(null);
    try {
      await printBytes(buildTestEscPos(config.storeName));
      setInfo('Test page sent.');
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const refresh = useCallback(async () => {
    try {
      if (native) setNativeDevices(await nativeListDevices());
      else if (usb) setDevices(await usb.getDevices());
    } catch (e) {
      setError((e as Error).message);
    }
  }, [usb, native]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const connect = async () => {
    if (!usb) return;
    setError(null);
    setBusy(true);
    try {
      // Empty filters → the picker lists every connected USB device.
      await usb.requestDevice({ filters: [] });
      await refresh();
    } catch (e) {
      // The user dismissing the chooser throws; treat that as a no-op.
      const msg = (e as Error).message || '';
      if (!/No device selected/i.test(msg)) setError(msg);
    } finally {
      setBusy(false);
    }
  };

  const toggleOpen = async (d: WebUsbDevice) => {
    setError(null);
    try {
      if (d.opened) await d.close();
      else await d.open();
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const forget = async (d: WebUsbDevice) => {
    setError(null);
    try {
      if (d.forget) await d.forget();
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="section-page">
      <div className="page-header">
        <h1>USB Devices: {native ? nativeDevices.length : devices.length}</h1>
        {native ? (
          <button className="btn-teal" onClick={refresh} style={{ padding: '8px 16px', borderRadius: 6 }}>
            ⟳ Refresh
          </button>
        ) : (
          usb && (
            <button className="btn-teal" onClick={connect} disabled={busy} style={{ padding: '8px 16px', borderRadius: 6 }}>
              {busy ? 'Waiting…' : '+ Connect USB Device'}
            </button>
          )
        )}
      </div>

      {native ? (
        <div className="section-card">
          <h2>Connected devices</h2>
          <p className="muted" style={{ marginBottom: 10 }}>
            Default print device:{' '}
            {printer ? (
              <>
                <strong>{printer.label || `${hex(printer.vendorId)}/${hex(printer.productId)}`}</strong>{' '}
                <button className="btn-grey" onClick={testPrint}>
                  Test Print
                </button>{' '}
                <button className="btn-grey" onClick={unsetPrinter}>
                  Clear
                </button>
              </>
            ) : (
              <em>none — receipts/KOTs use the system print dialog.</em>
            )}
          </p>
          <p className="muted" style={{ marginBottom: 10 }}>
            Label print device:{' '}
            {labelPrinter ? (
              <>
                <strong>{labelPrinter.label || `${hex(labelPrinter.vendorId)}/${hex(labelPrinter.productId)}`}</strong>{' '}
                <button className="btn-grey" onClick={testLabelPrint}>
                  Test Label
                </button>{' '}
                <button className="btn-grey" onClick={unsetLabelPrinter}>
                  Clear
                </button>
              </>
            ) : printer ? (
              <>
                <em>none — barcode labels go to {printer.label || 'the default printer'} as ESC/POS.</em>{' '}
                <button className="btn-grey" onClick={testLabelPrint}>
                  Test Label
                </button>
              </>
            ) : (
              <em>none — barcode labels use the system print dialog.</em>
            )}
          </p>
          {error && <p style={{ color: 'var(--color-accent)' }}>{error}</p>}
          {info && <p style={{ color: 'var(--color-primary-dark)' }}>{info}</p>}
          {nativeDevices.length === 0 ? (
            <p className="muted">No USB devices found. Plug the printer in (USB OTG) and tap Refresh.</p>
          ) : (
            <table className="section-table">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Manufacturer</th>
                  <th>VID / PID</th>
                  <th>Access</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {nativeDevices.map((d, i) => (
                  <tr key={`${d.vendorId}-${d.productId}-${i}`}>
                    <td>
                      {d.isPrinter ? '🖨️ ' : ''}
                      {d.productName || 'USB Device'}
                    </td>
                    <td>{d.manufacturerName || '—'}</td>
                    <td>
                      {hex(d.vendorId)} / {hex(d.productId)}
                    </td>
                    <td>
                      <span className={`badge ${d.hasPermission ? 'completed' : 'cancelled'}`}>
                        {d.hasPermission ? 'allowed' : 'no permission'}
                      </span>
                    </td>
                    <td>
                      {printer && printer.vendorId === d.vendorId && printer.productId === d.productId ? (
                        <button className="btn-grey" onClick={unsetPrinter}>
                          ★ Printer
                        </button>
                      ) : (
                        <button className="btn-teal" onClick={() => setAsNativePrinter(d)}>
                          Set as Printer
                        </button>
                      )}{' '}
                      {isLabelPrinter(d) ? (
                        <button className="btn-grey" onClick={unsetLabelPrinter}>
                          ★ Label Printer
                        </button>
                      ) : (
                        <button className="btn-teal" onClick={() => setAsLabelPrinter(d)}>
                          Set as Label Printer
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : !usb ? (
        <div className="section-card">
          <p className="muted">
            WebUSB isn't available in this browser. Open the app in Chrome or Edge over{' '}
            <code>localhost</code> or HTTPS to connect USB printers/scanners. (Firefox and Safari
            don't support WebUSB.)
          </p>
        </div>
      ) : (
        <div className="section-card">
          <h2>Authorized devices</h2>
          <p className="muted" style={{ marginBottom: 10 }}>
            Default print device:{' '}
            {printer ? (
              <>
                <strong>{printer.label || `${hex(printer.vendorId)}/${hex(printer.productId)}`}</strong>{' '}
                <button className="btn-grey" onClick={testPrint}>
                  Test Print
                </button>{' '}
                <button className="btn-grey" onClick={unsetPrinter}>
                  Clear
                </button>
              </>
            ) : (
              <em>none — receipts/KOTs use the browser print dialog.</em>
            )}
          </p>
          <p className="muted" style={{ marginBottom: 10 }}>
            Label print device:{' '}
            {labelPrinter ? (
              <>
                <strong>{labelPrinter.label || `${hex(labelPrinter.vendorId)}/${hex(labelPrinter.productId)}`}</strong>{' '}
                <button className="btn-grey" onClick={testLabelPrint}>
                  Test Label
                </button>{' '}
                <button className="btn-grey" onClick={unsetLabelPrinter}>
                  Clear
                </button>
              </>
            ) : printer ? (
              <>
                <em>none — barcode labels go to {printer.label || 'the default printer'} as ESC/POS.</em>{' '}
                <button className="btn-grey" onClick={testLabelPrint}>
                  Test Label
                </button>
              </>
            ) : (
              <em>none — barcode labels use the browser print sheet.</em>
            )}
          </p>
          {error && <p style={{ color: 'var(--color-accent)' }}>{error}</p>}
          {info && <p style={{ color: 'var(--color-primary-dark)' }}>{info}</p>}
          {devices.length === 0 ? (
            <p className="muted">
              No USB devices linked yet. Plug in a device and tap “Connect USB Device”, then pick it
              from the browser prompt.
            </p>
          ) : (
            <table className="section-table">
              <thead>
                <tr>
                  <th>Device</th>
                  <th>Manufacturer</th>
                  <th>VID / PID</th>
                  <th>Serial</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d, i) => (
                  <tr key={`${d.vendorId}-${d.productId}-${i}`}>
                    <td>{d.productName || 'USB Device'}</td>
                    <td>{d.manufacturerName || '—'}</td>
                    <td>
                      {hex(d.vendorId)} / {hex(d.productId)}
                    </td>
                    <td>{d.serialNumber || '—'}</td>
                    <td>
                      <span className={`badge ${d.opened ? 'completed' : 'cancelled'}`}>
                        {d.opened ? 'connected' : 'idle'}
                      </span>
                    </td>
                    <td>
                      {isPrinter(d) ? (
                        <button className="btn-grey" onClick={unsetPrinter}>
                          ★ Printer
                        </button>
                      ) : (
                        <button className="btn-teal" onClick={() => setAsPrinter(d)}>
                          Set as Printer
                        </button>
                      )}{' '}
                      {isLabelPrinter(d) ? (
                        <button className="btn-grey" onClick={unsetLabelPrinter}>
                          ★ Label Printer
                        </button>
                      ) : (
                        <button className="btn-teal" onClick={() => setAsLabelPrinter(d)}>
                          Set as Label Printer
                        </button>
                      )}{' '}
                      <button className="btn-grey" onClick={() => toggleOpen(d)}>
                        {d.opened ? 'Disconnect' : 'Connect'}
                      </button>{' '}
                      {d.forget && (
                        <button className="btn-grey" onClick={() => forget(d)}>
                          Forget
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default UsbLink;
